import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { ArrowRight, Play, BookOpen, Mic, Camera, ChevronDown } from 'lucide-react';

const IMG = {
  hero: 'https://customer-assets.emergentagent.com/job_keepsake-stories/artifacts/gez0irbl_image%201%20top%20baseball.png',
  box: 'https://customer-assets.emergentagent.com/job_keepsake-stories/artifacts/6lkvomdx_Image%202%20Black%20Keepsake%20Box.png',
  opening: 'https://customer-assets.emergentagent.com/job_keepsake-stories/artifacts/smfmvq3j_Image%203%20Hands%20opening%20box.png',
  book: 'https://customer-assets.emergentagent.com/job_keepsake-stories/artifacts/hxr8bism_image%204%20biography%20book.png',
  letter: 'https://customer-assets.emergentagent.com/job_keepsake-stories/artifacts/y2f60bew_image%205%20handwritten%20letter.png',
  photos: 'https://customer-assets.emergentagent.com/job_keepsake-stories/artifacts/qlgh6xaj_image%206%20curated%20photos.png',
  watching: 'https://customer-assets.emergentagent.com/job_keepsake-stories/artifacts/r1lzf3dn_image%207%20watching%20video.png',
  nfc: 'https://customer-assets.emergentagent.com/job_keepsake-stories/artifacts/def8qj5o_image%208%20nfc%20access%20card.png',
  hearing: 'https://customer-assets.emergentagent.com/job_keepsake-stories/artifacts/vghxcxf8_image%209%20hearing%20his%20voice.png',
};

export default function HomePage() {
  const [showKeepsakes, setShowKeepsakes] = useState(false);

  return (
    <div className="min-h-screen bg-[#FCFBF9]">
      <SEO
        path="/"
        description="Preserve your parent's voice, stories, and life lessons through a professionally guided legacy recording. Turned into a tangible heirloom that lasts for generations. Serving Austin, Dallas, Houston, and more."
      />
      <Navbar />

      {/* ========== IMAGE 1 — HERO ========== */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-end">
        <div className="absolute inset-0">
          <img src={IMG.hero} alt="Father holding baseball photo — legacy, memory, family" className="w-full h-full object-cover" style={{ objectPosition: '50% 30%' }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917] via-[#1C1917]/50 to-[#1C1917]/10" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-6 sm:px-12 pb-16 sm:pb-24 w-full pt-32">
          <div className="animate-fade-in-up">
            <p className="text-[11px] sm:text-xs text-[#A89F91]/80 tracking-[0.25em] uppercase mb-6">
              Stories meant to last &nbsp;&middot;&nbsp; Select markets &nbsp;&middot;&nbsp; Guided support
            </p>
            <h1 className="font-['Cormorant_Garamond'] text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-medium text-[#FCFBF9] leading-[1.02] tracking-tight mb-6">
              Their voice.<br />Their story.<br />
              <span className="italic text-[#A89F91]">Forever yours.</span>
            </h1>
            <p className="text-base sm:text-lg text-[#A89F91] leading-relaxed max-w-lg mb-4">
              We preserve a parent's voice, stories, and life lessons through a professionally guided recording — turned into a tangible heirloom that lasts for generations.
            </p>
            <p className="text-sm text-[#A89F91]/60 mb-10">
              Private &middot; Family-controlled &middot; No technical work required.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" data-testid="hero-cta-primary" className="inline-flex items-center justify-center bg-[#FCFBF9] text-[#1C1917] hover:bg-[#F2EFE9] px-8 py-4 text-sm font-medium tracking-wide transition-all duration-300 gap-2">
                Begin Your Story <ArrowRight size={16} />
              </Link>
              <Link to="/how-it-works" data-testid="hero-cta-secondary" className="inline-flex items-center justify-center border border-[#A89F91]/40 text-[#FCFBF9] hover:border-[#FCFBF9]/60 px-8 py-4 text-sm font-medium tracking-wide transition-all duration-300">
                Learn How It Works
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why This Matters */}
      <section data-testid="why-section" className="py-24 sm:py-32 bg-[#F2EFE9]">
        <div className="max-w-4xl mx-auto px-6 sm:px-12 text-center">
          <p className="overline mb-6">Why This Matters</p>
          <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-medium text-[#1C1917] leading-tight mb-8">
            Photos won't preserve this.
          </h2>
          <p className="text-base sm:text-lg text-[#57534E] leading-relaxed max-w-2xl mx-auto mb-6">
            Most families have thousands of photos and videos. Almost none have their parent's voice, stories, or life lessons captured clearly, intentionally, and in one place.
          </p>
          <p className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl italic text-[#78716C]">
            People don't regret doing this. They regret waiting.
          </p>
        </div>
      </section>

      {/* What We Do */}
      <section data-testid="what-we-do-section" className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="text-center mb-16 sm:mb-20">
            <p className="overline mb-6">What We Help You Do</p>
            <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-medium text-[#1C1917] leading-tight">
              A legacy worth keeping
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
            {[
              { icon: <Mic size={28} />, title: 'Preserve Their Voice', desc: 'Capture how your parent sounds, not just how they look.' },
              { icon: <BookOpen size={28} />, title: 'Capture Their Stories', desc: 'Record stories you didn\'t know to ask about.' },
              { icon: <Play size={28} />, title: 'Record Life Lessons', desc: 'Wisdom, values, and lessons for future generations.' },
              { icon: <Camera size={28} />, title: 'Create an Heirloom', desc: 'Something meaningful, permanent, and irreplaceable.' },
            ].map((item, i) => (
              <div key={i} className="opacity-0 animate-fade-in-up" style={{ animationDelay: `${i * 150}ms`, animationFillMode: 'forwards' }}>
                <div className="text-[#A89F91] mb-4">{item.icon}</div>
                <h3 className="font-['Cormorant_Garamond'] text-xl sm:text-2xl font-medium text-[#1C1917] mb-3">{item.title}</h3>
                <p className="text-sm text-[#57534E] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section data-testid="process-section" className="py-24 sm:py-32 bg-[#1C1917] text-[#FCFBF9]">
        <div className="max-w-5xl mx-auto px-6 sm:px-12">
          <div className="text-center mb-16 sm:mb-20">
            <p className="overline text-[#A89F91] mb-6">The Process</p>
            <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-medium leading-tight">
              A white-glove, guided experience
            </h2>
          </div>
          <div className="space-y-12">
            {[
              { step: '01', title: 'Choose Your Package', desc: 'Select the legacy planning package that feels right for your family.' },
              { step: '02', title: 'Family Intake Call', desc: 'A 30\u201340 minute conversation with your coordinator to understand your loved one and customize the session.' },
              { step: '03', title: 'The Guided Recording', desc: 'About 2 hours, paced to your loved one\u2019s comfort. We handle all the equipment \u2014 you just be present.' },
              { step: '04', title: 'Receive Your Heirloom', desc: 'Your completed heirloom \u2014 physical box and private digital experience \u2014 delivered within approximately two weeks.' },
            ].map((item, i) => (
              <div key={i} className="flex gap-8 items-start opacity-0 animate-fade-in-up" style={{ animationDelay: `${i * 200}ms`, animationFillMode: 'forwards' }}>
                <span className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-light text-[#A89F91] shrink-0 w-16">{item.step}</span>
                <div>
                  <h3 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-medium mb-2">{item.title}</h3>
                  <p className="text-sm sm:text-base text-[#A89F91] leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== IMAGE 2 — THE KEEPSAKE BOX ========== */}
      <section data-testid="heirloom-box-section" className="py-0">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="bg-[#FCFBF9] flex items-center justify-center py-20 sm:py-28 px-6 sm:px-12">
            <div className="max-w-md">
              <p className="overline mb-6">The Heirloom</p>
              <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-medium text-[#1C1917] leading-tight mb-6">
                A box arrives at your door.
              </h2>
              <p className="text-base text-[#57534E] leading-relaxed">
                Premium magnetic closure. Gold foil detailing. Built to sit on a shelf for generations. Inside, a lifetime of stories — preserved with care.
              </p>
            </div>
          </div>
          <div className="relative min-h-[400px] sm:min-h-[560px]">
            <img src={IMG.box} alt="Premium Heirloom Studios black keepsake box" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* ========== IMAGE 3 — HANDS OPENING BOX ========== */}
      <section data-testid="heirloom-experience-section" className="relative">
        <div className="relative min-h-[70vh] sm:min-h-[80vh] flex items-end">
          <img src={IMG.opening} alt="Family opening the Heirloom Studios box together" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: '50% 25%' }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917]/70 via-[#1C1917]/20 to-transparent" />
          <div className="relative z-10 max-w-4xl mx-auto px-6 sm:px-12 pb-16 sm:pb-24 w-full">
            <p className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl lg:text-4xl italic text-[#FCFBF9] leading-snug max-w-xl">
              The moment you lift the lid — everything changes.
            </p>
          </div>
        </div>
      </section>

      {/* ========== IMAGE 4 — BIOGRAPHY BOOK ========== */}
      <section data-testid="biography-book-section" className="py-0">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="relative min-h-[400px] sm:min-h-[560px] order-2 lg:order-1">
            <img src={IMG.book} alt="Life story book — The Measure of a Man" className="w-full h-full object-cover" style={{ objectPosition: '50% 40%' }} />
          </div>
          <div className="bg-[#F2EFE9] flex items-center justify-center py-20 sm:py-28 px-6 sm:px-12 order-1 lg:order-2">
            <div className="max-w-md">
              <p className="overline mb-6">The Life Story Book</p>
              <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-medium text-[#1C1917] leading-tight mb-6">
                Their story. Printed. Bound. Permanent.
              </h2>
              <p className="text-base text-[#57534E] leading-relaxed">
                A professionally written biography, illustrated with photographs from their life. Hardcover. Designed to be read, revisited, and passed down.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== IMAGE 5 — HANDWRITTEN LETTER ========== */}
      <section data-testid="letter-section" className="relative">
        <div className="relative min-h-[70vh] sm:min-h-[85vh] flex items-center justify-center">
          <img src={IMG.letter} alt="Handwritten letter from Dad to his children" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C1917]/20" />
        </div>
      </section>

      {/* ========== IMAGE 6 — CURATED PHOTOS ========== */}
      <section data-testid="curated-photos-section" className="relative">
        <div className="relative min-h-[70vh] sm:min-h-[85vh] flex items-end">
          <img src={IMG.photos} alt="Curated family photographs and life timeline" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917]/70 via-transparent to-transparent" />
          <div className="relative z-10 max-w-4xl mx-auto px-6 sm:px-12 pb-16 sm:pb-24 w-full">
            <p className="overline text-[#A89F91] mb-4">Curated Memories</p>
            <p className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl lg:text-4xl italic text-[#FCFBF9] leading-snug max-w-lg">
              Every milestone. Every chapter. Preserved in one place.
            </p>
          </div>
        </div>
      </section>

      {/* ========== IMAGE 7 — WATCHING VIDEO ========== */}
      <section data-testid="reveal-moment-section" className="py-0">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="bg-[#1C1917] flex items-center justify-center py-20 sm:py-28 px-6 sm:px-12">
            <div className="max-w-md">
              <p className="overline text-[#A89F91] mb-6">The Story Film</p>
              <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-medium text-[#FCFBF9] leading-tight mb-6">
                You press play.<br />
                <span className="italic text-[#A89F91]">And they're right there.</span>
              </h2>
              <p className="text-base text-[#A89F91] leading-relaxed">
                A professionally edited film of their life story. Their voice. Their laughter. Their wisdom. Carefully produced into something your family will return to for generations.
              </p>
            </div>
          </div>
          <div className="relative min-h-[400px] sm:min-h-[560px]">
            <img src={IMG.watching} alt="Family watching their loved one's story film together" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* ========== IMAGE 8 — NFC ACCESS CARD ========== */}
      <section data-testid="access-card-section" className="py-0">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="relative min-h-[400px] sm:min-h-[560px] order-2 lg:order-1">
            <img src={IMG.nfc} alt="Tapping the NFC access card to open a loved one's story" className="w-full h-full object-cover" />
          </div>
          <div className="bg-[#FCFBF9] flex items-center justify-center py-20 sm:py-28 px-6 sm:px-12 order-1 lg:order-2">
            <div className="max-w-md">
              <p className="overline mb-6">Private Access</p>
              <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-medium text-[#1C1917] leading-tight mb-6">
                Tap. Watch. Listen.
              </h2>
              <p className="text-base text-[#57534E] leading-relaxed mb-6">
                A premium NFC access card — included in every heirloom box. Hold it to your phone and their private digital experience opens instantly. No apps. No accounts. No downloads.
              </p>
              <p className="text-sm text-[#A89F91] italic">
                Simple, private access. Preserved forever.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== IMAGE 9 — HEARING HIS VOICE ========== */}
      <section data-testid="hearing-voice-section" className="relative">
        <div className="relative min-h-[80vh] sm:min-h-[90vh] flex items-end">
          <img src={IMG.hearing} alt="A woman listening to her father's voice recording" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: '30% 50%' }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917]/60 via-transparent to-transparent" />
          <div className="relative z-10 max-w-4xl mx-auto px-6 sm:px-12 pb-16 sm:pb-24 w-full">
            <p className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl lg:text-4xl italic text-[#FCFBF9]/90 leading-snug max-w-md">
              Hearing his voice again.
            </p>
          </div>
        </div>
      </section>

      {/* ========== WHAT'S INSIDE THE BOX ========== */}
      <section data-testid="whats-inside-section" className="py-28 sm:py-36 bg-[#F2EFE9]">
        <div className="max-w-5xl mx-auto px-6 sm:px-12">
          <div className="text-center mb-16 sm:mb-20">
            <p className="overline mb-6">What's Inside</p>
            <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-medium text-[#1C1917] leading-tight">
              Everything your family needs to remember.
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-12 sm:gap-y-16">
            {[
              { label: 'Premium Keepsake Box', desc: 'Magnetic closure, built to last generations', icon: '01' },
              { label: 'Story Film', desc: 'Professionally edited video of their life story', icon: '02' },
              { label: 'Audio Recordings', desc: 'Their voice preserved in high-quality audio', icon: '03' },
              { label: 'Life Story Book', desc: 'Professionally written, printed, and bound', icon: '04' },
              { label: 'Curated Photo Prints', desc: 'Selected photographs from their life', icon: '05' },
              { label: 'Sealed Envelope', desc: 'A personal letter for the family', icon: '06' },
              { label: 'NFC Access Card', desc: 'Tap to open their private digital heirloom', icon: '07' },
              { label: 'Legacy Letter', desc: 'Their own words, preserved in their hand', icon: '08' },
            ].map((item, i) => (
              <div key={i} data-testid={`box-item-${i}`} className="text-center sm:text-left">
                <span className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-light text-[#D6D3CD] block mb-3">{item.icon}</span>
                <h3 className="font-['Cormorant_Garamond'] text-lg sm:text-xl font-medium text-[#1C1917] mb-1.5">{item.label}</h3>
                <p className="text-sm text-[#78716C] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-16 sm:mt-20">
            <p className="font-['Cormorant_Garamond'] text-xl sm:text-2xl italic text-[#78716C]">
              A complete archive. Physical and digital. Built to last.
            </p>
          </div>

          {/* Personalized Keepsakes Subsection */}
          <div className="mt-20 sm:mt-28 pt-16 sm:pt-20 border-t border-[#D6D3CD]/50" data-testid="personalized-keepsakes-section">
            <div className="text-center mb-10 sm:mb-12">
              <p className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-medium text-[#1C1917] mb-4">
                Built around their life.
              </p>
              <p className="text-sm sm:text-base text-[#78716C] leading-relaxed max-w-xl mx-auto">
                Every family is different. Many Heirlooms also include custom pieces designed specifically around your loved one's voice, memories, and traditions.
              </p>
            </div>

            {!showKeepsakes ? (
              <div className="text-center">
                <button
                  onClick={() => setShowKeepsakes(true)}
                  data-testid="show-keepsakes-button"
                  className="inline-flex items-center gap-2 text-sm text-[#57534E] border border-[#D6D3CD] hover:border-[#A89F91] hover:text-[#1C1917] px-6 py-3 transition-all duration-300"
                >
                  View personalized options <ChevronDown size={14} />
                </button>
              </div>
            ) : (
              <div className="animate-fade-in">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-8 sm:gap-y-10">
                  {[
                    { title: 'Fingerprint Keepsake', desc: 'A wearable or display piece created from your loved one\u2019s fingerprint.' },
                    { title: 'Voice Wave Art', desc: 'A framed soundwave print paired with playback of their actual voice.' },
                    { title: 'Heartbeat Recording', desc: 'A preserved heartbeat transformed into a framed visual keepsake with audio.' },
                    { title: 'Handwriting Preservation', desc: 'Meaningful handwritten phrases preserved on archival paper.' },
                    { title: 'Handwritten Recipe Cards', desc: 'Family recipes preserved alongside the stories behind them.' },
                    { title: 'Scent Preservation Vial', desc: 'A preserved personal scent connected to memory and presence.' },
                    { title: 'Memorial Fabric Square', desc: 'A fabric keepsake created from meaningful clothing or textiles.' },
                    { title: 'Life Lessons Cards', desc: 'A collection of wisdom cards drawn from their interview and memories.' },
                    { title: 'Story Starter Cards', desc: 'Conversation cards designed to pass stories between generations.' },
                    { title: 'Life Timeline Poster', desc: 'A large-format visual timeline of their life and milestones.' },
                  ].map((item, i) => (
                    <div
                      key={i}
                      data-testid={`keepsake-${i}`}
                      className="group opacity-0 animate-fade-in-up"
                      style={{ animationDelay: `${i * 80}ms`, animationFillMode: 'forwards' }}
                    >
                      <h4 className="font-['Cormorant_Garamond'] text-lg font-medium text-[#1C1917] mb-1.5 group-hover:text-[#57534E] transition-colors duration-300">
                        {item.title}
                      </h4>
                      <p className="text-sm text-[#A89F91] leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
                <div className="text-center mt-12 sm:mt-16 space-y-3">
                  <p className="font-['Cormorant_Garamond'] text-lg italic text-[#78716C]">
                    No two Heirlooms are ever exactly alike.
                  </p>
                  <p className="text-xs text-[#A89F91]">
                    Available options vary by family and package.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section data-testid="testimonials-section" className="py-24 sm:py-32 bg-[#F2EFE9]">
        <div className="max-w-5xl mx-auto px-6 sm:px-12">
          <p className="overline text-center mb-16">Reflections From Families</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-14">
            {[
              { quote: "I didn\u2019t realize how much I needed this until we finished. Hearing my father tell stories I\u2019d never heard \u2014 in his own voice \u2014 that changed something in me.", name: "Sarah M.", relationship: "Daughter", location: "Austin, TX" },
              { quote: "This is the most meaningful thing I\u2019ve ever done for my family. My kids will grow up hearing their grandmother\u2019s voice. That\u2019s priceless.", name: "James R.", relationship: "Son", location: "Dallas, TX" },
              { quote: "The whole process felt effortless. They handled everything with such care. When we opened the box, my mother cried \u2014 happy tears.", name: "Maria L.", relationship: "Daughter", location: "Houston, TX" },
              { quote: "I only wish I had done it sooner. If you\u2019re thinking about it, don\u2019t wait. You\u2019ll never regret preserving their story.", name: "David K.", relationship: "Son", location: "Denver, CO" },
            ].map((t, i) => (
              <div key={i} data-testid={`testimonial-${i}`} className="border-l border-[#D6D3CD] pl-6 sm:pl-8">
                <p className="font-['Cormorant_Garamond'] text-lg sm:text-xl italic text-[#1C1917] leading-relaxed mb-5">
                  "{t.quote}"
                </p>
                <div>
                  <p className="text-sm font-medium text-[#1C1917]">{t.name}</p>
                  <p className="text-xs text-[#A89F91]">{t.relationship} &middot; {t.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="faq-section" className="py-24 sm:py-32">
        <div className="max-w-3xl mx-auto px-6 sm:px-12">
          <div className="text-center mb-16">
            <p className="overline mb-6">Common Questions</p>
            <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-medium text-[#1C1917] leading-tight">
              Questions families ask
            </h2>
          </div>
          <div className="space-y-8">
            {[
              { q: "Is this available where I live?", a: "We currently serve select markets including Austin, Dallas, Houston, and other cities. If your area isn\u2019t listed, reach out \u2014 we\u2019re expanding and may be able to accommodate your family." },
              { q: "How long is the session?", a: "The recording session is about 2 hours, paced entirely to your loved one\u2019s comfort. Before that, you\u2019ll have a 30\u201340 minute intake call with your coordinator to plan everything." },
              { q: "Who controls the content?", a: "Your family does. The heirloom is private and family-controlled. You decide who has access, and the content is never shared without your permission." },
              { q: "When do we receive everything?", a: "Approximately two weeks after the recording session, your family receives the physical heirloom box and access to the private digital experience." },
              { q: "Is any technical work required?", a: "None. Simple, private access \u2014 no apps, no accounts, no downloads. Just tap the NFC card or scan the QR code and the experience opens in your browser." },
            ].map((item, i) => (
              <div key={i} data-testid={`faq-${i}`} className="border-b border-[#E7E5DF] pb-8">
                <h3 className="font-['Cormorant_Garamond'] text-xl sm:text-2xl font-medium text-[#1C1917] mb-3">{item.q}</h3>
                <p className="text-sm sm:text-base text-[#57534E] leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section data-testid="final-cta-section" className="py-24 sm:py-32 text-center">
        <div className="max-w-3xl mx-auto px-6 sm:px-12">
          <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-6xl font-medium text-[#1C1917] leading-tight mb-6">
            You're not late.<br />
            <span className="italic text-[#78716C]">But you're not early either.</span>
          </h2>
          <p className="text-base sm:text-lg text-[#57534E] mb-10">
            Stories meant to last.
          </p>
          <Link to="/packages" data-testid="final-cta-button" className="inline-flex items-center justify-center bg-[#1C1917] text-[#FCFBF9] hover:bg-[#322f2d] px-10 py-4 text-sm font-medium tracking-wide transition-all duration-300 gap-2">
            View Legacy Planning Packages <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
